import { Button, Group, Stack, Text, useComputedColorScheme } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { ComponentProps, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';

import { formatTravelNumber } from '../TravelAnalyticsUtils';
import { getWorldTopologyOptions } from '../model/TravelRegistryQuery';
import { TravelCountryAggregate } from '../model/TravelRegistryTypes';
import { countryNumericCode } from './countryNumericCode';

interface TravelCountryMapPanelProps {
  byCountry: TravelCountryAggregate[];
}

type GeographyProp = ComponentProps<typeof Geographies>['geography'];

const MAP_HEIGHT = 320;
const LABEL_MIN_WIDTH = 11;
const LABEL_MIN_HEIGHT = 8;
const SCALE_STEPS = 4;

const MAP_COLORS = {
  light: {
    empty: '#e9ecef',
    stroke: '#ffffff',
    scale: ['#c5dcf5', '#8bb9e8', '#5195db', '#2a78d6'],
    labelOnLight: '#1b2a41',
    labelOnDark: '#ffffff',
    selectedStroke: '#1b2a41',
  },
  dark: {
    empty: '#343a40',
    stroke: '#1a1b1e',
    scale: ['#1f4468', '#2a5f96', '#3173bd', '#3987e5'],
    labelOnLight: '#e9ecef',
    labelOnDark: '#ffffff',
    selectedStroke: '#ffffff',
  },
};

// react-simple-maps types the render prop's `path` as a string builder, but it hands over the d3 geoPath itself.
interface GeoPath {
  centroid: (geo: unknown) => [number, number];
  bounds: (geo: unknown) => [[number, number], [number, number]];
}

// Colour tracks trips, never money: a country can have real travel and 0 EUR, and must not read as untouched.
function scaleIndex(trips: number, maxTrips: number) {
  if (trips < 1) {
    return 0;
  }
  if (maxTrips <= 1) {
    return SCALE_STEPS - 1;
  }
  const position = (trips - 1) / (maxTrips - 1);
  return Math.min(SCALE_STEPS - 1, Math.floor(position * SCALE_STEPS));
}

export function TravelCountryMapPanel({ byCountry }: TravelCountryMapPanelProps) {
  const { t } = useTranslation();
  const scheme = useComputedColorScheme('light');
  const [selected, setSelected] = useState<{ key: string; caption: string } | null>(null);
  const { data: topology, isError, isPending } = useQuery(getWorldTopologyOptions());

  const colors = MAP_COLORS[scheme];

  if (isError) {
    return (
      <Text
        size="sm"
        c="dimmed"
      >
        {t('travel.registry.aggregates.map.error')}
      </Text>
    );
  }
  if (isPending || topology == null) {
    return (
      <Text
        size="sm"
        c="dimmed"
      >
        {t('travel.registry.aggregates.map.loading')}
      </Text>
    );
  }

  const shapeIds = new Set(topology.objects.countries.geometries.map(shape => String(shape.id)));
  const byNumeric = new Map<string, TravelCountryAggregate>();
  // The 110m outline has no shape for the smallest countries, so their rows are listed under the map instead.
  const offMap: TravelCountryAggregate[] = [];
  byCountry.forEach(row => {
    const numeric = countryNumericCode(row.countryCode);
    if (numeric != null && shapeIds.has(numeric)) {
      byNumeric.set(numeric, row);
    } else if (row.country !== '') {
      offMap.push(row);
    }
  });
  const maxTrips = Math.max(...[...byNumeric.values(), ...offMap].map(row => row.trips), 0);
  const captionOf = (row: TravelCountryAggregate) =>
    t('travel.registry.aggregates.map.tooltip', {
      country: row.country,
      count: row.trips,
      amount: formatTravelNumber(row.amountEur),
    });

  return (
    <Stack
      gap="xs"
      data-testid="travel-country-map"
    >
      <Text
        size="sm"
        fw="500"
      >
        {t('travel.registry.aggregates.map.title')}
      </Text>
      <ComposableMap
        projection="geoEqualEarth"
        height={MAP_HEIGHT}
        projectionConfig={{ scale: 150 }}
        aria-label={t('travel.registry.aggregates.map.title')}
      >
        {/* The runtime converts a Topology itself, but the prop type only admits GeoJSON. */}
        <Geographies geography={topology as unknown as GeographyProp}>
          {({ geographies, path }) =>
            geographies.map(geo => {
              const row = geo.id == null ? undefined : byNumeric.get(String(geo.id));
              const step = row ? scaleIndex(row.trips, maxTrips) : -1;
              const caption = row && captionOf(row);
              const geoPath = path as unknown as GeoPath;
              const [[x0, y0], [x1, y1]] = geoPath.bounds(geo);
              const roomForLabel = x1 - x0 >= LABEL_MIN_WIDTH && y1 - y0 >= LABEL_MIN_HEIGHT;
              const [cx, cy] = geoPath.centroid(geo);

              const isSelected = selected?.key === geo.rsmKey;

              return (
                <g
                  key={geo.rsmKey}
                  role={row ? 'button' : undefined}
                  aria-label={caption}
                  onClick={row ? () => setSelected({ key: geo.rsmKey, caption: caption ?? '' }) : undefined}
                  style={row ? { cursor: 'pointer' } : undefined}
                >
                  {caption && <title>{caption}</title>}
                  <Geography
                    geography={geo}
                    fill={row ? colors.scale[step] : colors.empty}
                    stroke={isSelected ? colors.selectedStroke : colors.stroke}
                    strokeWidth={isSelected ? 1 : 0.3}
                    // The library marks every path tabIndex=0, and the browser's focus ring on a click reads as a halo.
                    style={{ outline: 'none' }}
                  />
                  {row && roomForLabel && Number.isFinite(cx) && Number.isFinite(cy) && (
                    <text
                      x={cx}
                      y={cy}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={9}
                      fontWeight={600}
                      fill={step >= 2 ? colors.labelOnDark : colors.labelOnLight}
                      style={{ pointerEvents: 'none' }}
                    >
                      {row.trips}
                    </text>
                  )}
                </g>
              );
            })
          }
        </Geographies>
      </ComposableMap>
      {offMap.length > 0 && (
        <Group
          gap="xs"
          data-testid="travel-country-map-off-map"
        >
          <Text
            size="xs"
            c="dimmed"
          >
            {t('travel.registry.aggregates.map.offMap')}
          </Text>
          {offMap.map(row => {
            const key = `off-map-${row.country}`;
            const caption = captionOf(row);

            return (
              <Button
                key={key}
                size="compact-xs"
                variant={selected?.key === key ? 'outline' : 'default'}
                aria-label={caption}
                aria-pressed={selected?.key === key}
                leftSection={
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      backgroundColor: colors.scale[scaleIndex(row.trips, maxTrips)],
                      borderRadius: 2,
                    }}
                  />
                }
                onClick={() => setSelected({ key, caption })}
              >
                {`${row.country}: ${formatTravelNumber(row.trips)}`}
              </Button>
            );
          })}
        </Group>
      )}
      <Text
        size="xs"
        c="dimmed"
        role="status"
        data-testid="travel-country-map-selection"
      >
        {selected?.caption ?? t('travel.registry.aggregates.map.selectHint')}
      </Text>
      <Group gap="xs">
        <Text
          size="xs"
          c="dimmed"
        >
          {t('travel.registry.aggregates.map.legendLow')}
        </Text>
        {colors.scale.map(color => (
          <div
            key={color}
            style={{ width: 24, height: 10, backgroundColor: color, borderRadius: 2 }}
          />
        ))}
        <Text
          size="xs"
          c="dimmed"
        >
          {t('travel.registry.aggregates.map.legendHigh')}
        </Text>
      </Group>
    </Stack>
  );
}
